import React, { useState } from 'react';
import { Modal, Button, Input, Dropdown } from '../UI';
import { Screen, ScreenFormData, Indicator } from '../../types/screen';

interface ScreenCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ScreenFormData) => void;
  screenToClone?: Screen;
  existingScreens: Screen[];
}

const SYMBOLS = [
  { value: 'AAPL', label: 'AAPL - Apple Inc.' },
  { value: 'GOOGL', label: 'GOOGL - Alphabet Inc.' },
  { value: 'MSFT', label: 'MSFT - Microsoft Corporation' },
  { value: 'AMZN', label: 'AMZN - Amazon.com Inc.' },
  { value: 'TSLA', label: 'TSLA - Tesla Inc.' },
  { value: 'META', label: 'META - Meta Platforms Inc.' },
  { value: 'NVDA', label: 'NVDA - NVIDIA Corporation' },
  { value: 'SPY', label: 'SPY - SPDR S&P 500 ETF' },
];

const TIMEFRAMES = [
  { value: '1m', label: '1 Minute' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
];

const ScreenCreateModal: React.FC<ScreenCreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  screenToClone,
  existingScreens,
}) => {
  const [formData, setFormData] = useState<ScreenFormData>({
    name: screenToClone ? `${screenToClone.name} (Copy)` : '',
    symbol: screenToClone?.symbol || '',
    timeframe: screenToClone?.timeframe || '',
    indicators: screenToClone?.indicators || [],
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const generateDefaultName = (): string => {
    const screenNumbers = existingScreens
      .map(s => {
        const match = s.name.match(/^Screen (\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    
    const nextNumber = screenNumbers.length > 0 ? Math.max(...screenNumbers) + 1 : 1;
    return `Screen ${nextNumber}`;
  };
  
  const handleNameChange = (value: string) => {
    setFormData(prev => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Screen name is required';
    } else if (existingScreens.some(s => s.name === formData.name.trim() && s.id !== screenToClone?.id)) {
      newErrors.name = 'A screen with this name already exists';
    }
    
    if (!formData.symbol) {
      newErrors.symbol = 'Symbol is required';
    }
    
    if (!formData.timeframe) {
      newErrors.timeframe = 'Timeframe is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    const finalData: ScreenFormData = {
      ...formData,
      name: formData.name.trim() || generateDefaultName(),
    };
    
    try {
      await onSubmit(finalData);
      handleClose();
    } catch (error) {
      console.error('Error creating screen:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    setFormData({
      name: '',
      symbol: '',
      timeframe: '',
      indicators: [],
    });
    setErrors({});
    onClose();
  };
  
  const handleUseDefaultName = () => {
    setFormData(prev => ({ ...prev, name: generateDefaultName() }));
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={screenToClone ? 'Clone Screen' : 'Create New Screen'}
      size="md"
      footer={
        <div className="flex justify-end space-x-3">
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            {screenToClone ? 'Clone Screen' : 'Create Screen'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Screen Name"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder={generateDefaultName()}
          error={errors.name}
          helperText="Leave empty to use default naming (Screen 1, Screen 2, etc.)"
        />
        
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleUseDefaultName}
            className="text-sm text-[#3B82F6] hover:text-blue-700"
          >
            Use Default Name
          </button>
        </div>
        
        <Dropdown
          label="Symbol"
          options={SYMBOLS}
          value={formData.symbol}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, symbol: value as string }));
            if (errors.symbol) {
              setErrors(prev => ({ ...prev, symbol: '' }));
            }
          }}
          placeholder="Select a symbol"
          error={errors.symbol}
        />
        
        <Dropdown
          label="Timeframe"
          options={TIMEFRAMES}
          value={formData.timeframe}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, timeframe: value as string }));
            if (errors.timeframe) {
              setErrors(prev => ({ ...prev, timeframe: '' }));
            }
          }}
          placeholder="Select a timeframe"
          error={errors.timeframe}
        />
        
        {screenToClone && formData.indicators.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Indicators to be cloned:
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              {formData.indicators.map((indicator) => (
                <li key={indicator.id}>• {indicator.name}</li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default ScreenCreateModal;

